


import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import api from "../../api/axiosInterceptor";

export default function EkycComponent({ onNext, onBack, onShowError, onTxnNo }) {
  const [loading, setLoading] = useState(false);
  const [ekycDone, setEkycDone] = useState(false);
  const [ekycFailed, setEkycFailed] = useState(false);
  const [ekycData, setEkycData] = useState(null);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [confirmOpenDialogOpen, setConfirmOpenDialogOpen] = useState(true); // show modal first
  const [error, setError] = useState("");
  const [checkingStatus, setCheckingStatus] = useState(false);

  const txnNoRef = useRef(null);
  const popupRef = useRef(null);
  const popupPollRef = useRef(null); // watches for popup close

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupPollRef.current) clearInterval(popupPollRef.current);
    };
  }, []);

  const fetchEkycUrl = async () => {
    setLoading(true);
    setEkycDone(false);
    setEkycFailed(false);
    setEkycData(null);
    setError("");

    try {
      const response = await api.post(`/auth/get-adhar-url/`);
      console.log("eKYC URL response:", response.data);

      if (response.data && response.data.ekyc_url) {
        // const { ekyc_url, txn_no } = response.data;
        // txnNoRef.current = txn_no;
        const { ekyc_url, txn_no } = response.data;

          txnNoRef.current = txn_no;

          // pass txn no to parent
          if (onTxnNo) {
            onTxnNo(txn_no);
          }
        // Open popup
        const popup = window.open(ekyc_url, "_blank", "width=900,height=650");
        popupRef.current = popup;

        // Watch for popup close, then check status
        popupPollRef.current = setInterval(() => {
          if (popup && popup.closed) {
            clearInterval(popupPollRef.current);
            checkAadhaarStatus(txn_no);
          }
        }, 1000);

      } else {
        setError("Failed to load Aadhaar eKYC URL");
        if (onShowError) onShowError("Failed to load Aadhaar eKYC URL");
      }
    } catch (err) {
      console.error("fetchEkycUrl error:", err);
      setError("Failed to load Aadhaar eKYC URL");
      if (onShowError) onShowError("Failed to load Aadhaar eKYC URL");
    } finally {
      setLoading(false);
    }
  };

  const checkAadhaarStatus = async (txnNo) => {
    setCheckingStatus(true);
    try {
      const res = await api.get(`/auth/aadhaar-status/?txnno=${txnNo}`);
      console.log("Status response:", res.data);
      if (res.data && res.data.status === "success") {
        setEkycDone(true);
        setEkycFailed(false);
        setEkycData(res.data.data);
      } else {
        setEkycFailed(true);
        setEkycDone(false);
      }
    } catch (e) {
      console.error("Status check error:", e);
      setEkycFailed(true);
      setEkycDone(false);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleConfirmOpen = () => {
    setConfirmOpenDialogOpen(false);
    fetchEkycUrl();
  };

  const handleCancelOpen = () => {
    setConfirmOpenDialogOpen(false);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Aadhaar e-KYC Verification
      </Typography>

      {/* Loading eKYC URL */}
      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2 }}>
          <CircularProgress size={24} />
          <Typography>Loading Aadhaar verification...</Typography>
        </Box>
      )}

      {/* Checking status after popup closes */}
      {checkingStatus && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 2 }}>
          <CircularProgress size={24} />
          <Typography>Checking verification status...</Typography>
        </Box>
      )}

      {/* Error */}
      {!loading && !checkingStatus && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Waiting — popup is open */}
      {!loading && !checkingStatus && !ekycDone && !ekycFailed && !error && !confirmOpenDialogOpen && (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            px: 2,
            border: "1px dashed #ccc",
            borderRadius: 2,
            backgroundColor: "#fafafa",
          }}
        >
          <CircularProgress size={20} sx={{ mb: 2 }} />
          <Typography variant="body1" sx={{ mb: 1 }}>
            Aadhaar verification tab is open.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Complete the OTP in that tab. Status will update automatically when
            you close it.
          </Typography>
          <Button variant="outlined" onClick={fetchEkycUrl} disabled={loading}>
            Reopen Aadhaar Tab
          </Button>
        </Box>
      )}

      {/* Success */}
      {ekycDone && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          Aadhaar eKYC verification completed successfully!
        </Alert>
      )}

      {/* Failed */}
      {ekycFailed && !ekycDone && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Aadhaar verification failed or was not completed. Please try again.
          <Box sx={{ mt: 1 }}>
            <Button size="small" variant="outlined" onClick={fetchEkycUrl}>
              Retry
            </Button>
          </Box>
        </Alert>
      )}

      {/* Navigation */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Box>
          <Button
            variant="outlined"
            onClick={() => setSkipDialogOpen(true)}
            sx={{ mr: 1 }}
          >
            Skip
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onNext}
            disabled={!ekycDone}
          >
            Next
          </Button>
        </Box>
      </Box>

      {/* Confirm Open Aadhaar Dialog — shown first before opening tab */}
      <Dialog
        open={confirmOpenDialogOpen}
        onClose={handleCancelOpen}
      >
        <DialogTitle>Aadhaar e-KYC Verification</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You will be redirected to the Aadhaar eKYC portal in a new tab to
            complete OTP-based verification. Please keep this page open and
            return after completing verification.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOpen} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmOpen} color="primary" autoFocus>
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skip Dialog */}
      <Dialog open={skipDialogOpen} onClose={() => setSkipDialogOpen(false)}>
        <DialogTitle>Skip Aadhaar e-KYC</DialogTitle>
        <DialogContent>
          <DialogContentText>
            If you skip Aadhaar verification, you will need to upload your PAN
            or License document in the next tab.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setSkipDialogOpen(false);
              onNext();
            }}
            color="primary"
            autoFocus
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}



